/**
 * Beslutsläge Translations
 * UI text for the decision state feature
 * 
 * Skogskalkyl = von der Zahl zur Entscheidung
 * ForestHub = vom Fall zur Entscheidung
 */

export const beslutslageTranslations = {
  sv: {
    // Block 1: Resultat
    resultat: {
      title: 'Resultat',
      estimate: 'Uppskattning',
      range: 'Värdespann',
      basedOn: 'Baserat på',
      hectares: 'ha produktiv skogsmark',
      m3sk: 'm³sk virkesförråd'
    },
    
    // Block 2: Vad det betyder
    interpretation: {
      title: 'Vad resultatet betyder',
      regional: 'Jämfört med regionen',
      aboveAverage: 'Över regionsnitt',
      belowAverage: 'Under regionsnitt',
      atAverage: 'Nära regionsnitt',
      timberDriven: 'Virkesförrådet driver värdet mer än markläget.',
      landDriven: 'Markläget driver värdet mer än virkesförrådet.',
      balanced: 'Balans mellan virkes- och markvärde.'
    },
    
    // Block 3: Osäkerhet
    uncertainty: {
      title: 'Hur säkert är resultatet?',
      level: 'Osäkerhetsgrad',
      low: 'Låg',
      medium: 'Medel',
      high: 'Hög',
      factors: 'Viktiga faktorer:',
      volumeNotVerified: 'Virkesförråd: uppskattat, ej inventerat',
      priceRegional: 'Prisläge: baserat på regionsnitt',
      marketFactors: 'Marknadsfaktorer: ej inräknade',
      planMissing: 'Skogsbruksplan: saknas',
      rangeExplanation: 'Spannet ±15% reflekterar normal variation'
    },
    
    // Block 4: Vad som saknas
    missing: {
      title: 'Vad som saknas',
      intro: 'För ett säkrare beslut behövs:',
      inventory: 'Faktiskt virkesförråd (inventering)',
      forestPlan: 'Aktuell skogsbruksplan',
      marketInterest: 'Konkret köpare-/säljarintresse',
      localAssessment: 'Lokal marknadsanalys',
      siteQuality: 'Bonitetsbedömning'
    },
    
    // Block 5: Beslutsriktning
    direction: {
      title: 'Möjlig beslutsriktning',
      intro: 'Baserat på resultatet och nuvarande osäkerhet kan följande vara rimliga steg:',
      options: {
        keep: {
          title: 'Behålla och följa upp',
          desc: 'Om ingen brådska – avvakta och följ marknaden'
        },
        compare: {
          title: 'Jämföra vidare',
          desc: 'Inhämta fler värderingar eller jämförelseobjekt'
        },
        deepen: {
          title: 'Fördjupa underlaget',
          desc: 'Verifiera virkesförråd, bonitet eller skogsbruksplan'
        },
        external: {
          title: 'Ta in extern värdering',
          desc: 'Kontakta mäklare eller oberoende värderingsman'
        }
      },
      recommendation: 'Med nuvarande osäkerhet: fördjupa underlaget innan större beslut.'
    },
    
    // Block 6: Beslutsläge Premium Teaser
    premium: {
      title: 'Fördjupa beslutsunderlaget',
      question: 'Vill du förstå osäkerheterna bättre och se nästa rimliga steg?',
      intro: 'Beslutsläge hjälper dig att fördjupa underlaget för just detta ärende.',
      includes: 'Med Beslutsläge får du:',
      features: [
        'Lägesbild – vad resultatet faktiskt säger',
        'Osäkerheter – vad som kan tippa utfallet',
        'Nästa steg – 3–5 prioriterade åtgärder',
        'Beslutsbrev (PDF) – sammanfattning att spara eller dela'
      ],
      cta: 'Aktivera',
      learnMore: 'Läs om Beslutsläge',
      price: '149 kr',
      oneTime: 'Engångsbetalning – inget abonnemang',
      comingSoon: 'Kommer snart',
      notNow: 'Inte nu – fortsätt utan'
    },
    
    // Block 7: Spara
    save: {
      title: 'Spara & återkom',
      saveBtn: 'Spara beräkning',
      compareBtn: 'Jämför med annat fall',
      loginRequired: 'Logga in för att spara',
      saved: 'Sparad!',
      savedCalculations: 'Mina sparade beräkningar'
    },
    
    // Premium unlocked content
    premiumContent: {
      lagesbild: {
        title: 'Lägesbild',
        placeholder: 'Detaljerad analys av fastighetens värde och position på marknaden.'
      },
      osakerheter: {
        title: 'Osäkerheter',
        placeholder: 'Faktorer som kan påverka värdet uppåt eller nedåt.'
      },
      beslutsbehov: {
        title: 'Beslutsbehov',
        question: 'Räcker detta för beslut?',
        yes: 'Ja',
        no: 'Nej',
        partial: 'Delvis'
      },
      nastaSteg: {
        title: 'Nästa steg',
        placeholder: 'Prioriterade åtgärder för att komma vidare.'
      },
      pdf: {
        title: 'Beslutsbrev',
        download: 'Ladda ner beslutsbrev (PDF)'
      }
    },
    
    // Tier labels
    tiers: {
      free: 'Gratis',
      registered: 'Registrerad',
      premium: 'Premium'
    }
  },
  
  de: {
    resultat: {
      title: 'Ergebnis',
      estimate: 'Schätzung',
      range: 'Wertspanne',
      basedOn: 'Basierend auf',
      hectares: 'ha produktive Waldfläche',
      m3sk: 'm³ Holzvorrat'
    },
    
    interpretation: {
      title: 'Was das Ergebnis bedeutet',
      regional: 'Im Vergleich zur Region',
      aboveAverage: 'Über Regionsdurchschnitt',
      belowAverage: 'Unter Regionsdurchschnitt',
      atAverage: 'Nahe Regionsdurchschnitt',
      timberDriven: 'Der Holzvorrat treibt den Wert mehr als die Lage.',
      landDriven: 'Die Lage treibt den Wert mehr als der Holzvorrat.',
      balanced: 'Balance zwischen Holz- und Grundstückswert.'
    },
    
    uncertainty: {
      title: 'Wie sicher ist das Ergebnis?',
      level: 'Unsicherheitsgrad',
      low: 'Niedrig',
      medium: 'Mittel',
      high: 'Hoch',
      factors: 'Wichtige Faktoren:',
      volumeNotVerified: 'Holzvolumen: geschätzt, nicht inventarisiert',
      priceRegional: 'Preisniveau: basiert auf Regionsdurchschnitt',
      marketFactors: 'Marktfaktoren: nicht berücksichtigt',
      planMissing: 'Forstbetriebsplan: fehlt',
      rangeExplanation: 'Die Spanne ±15% reflektiert normale Variation'
    },
    
    missing: {
      title: 'Was fehlt',
      intro: 'Für eine sichere Entscheidung fehlt:',
      inventory: 'Tatsächliches Holzvolumen (Inventur)',
      forestPlan: 'Aktueller Forstbetriebsplan',
      marketInterest: 'Konkretes Kauf-/Verkaufsinteresse',
      localAssessment: 'Lokale Marktanalyse',
      siteQuality: 'Bonitätsbewertung'
    },
    
    direction: {
      title: 'Mögliche Entscheidungsrichtung',
      sell: 'Verkaufen',
      sellDesc: 'wenn der Markt stimmt',
      keep: 'Behalten',
      keepDesc: 'wenn kein akuter Bedarf',
      divide: 'Aufteilen',
      divideDesc: 'erfordert tiefere Prüfung',
      compare: 'Weiter vergleichen',
      compareDesc: 'weitere Bewertungen einholen',
      recommendation: 'Bei aktueller Unsicherheit: Unterlagen vertiefen vor Entscheidung.'
    },
    
    premium: {
      title: 'Entscheidungsgrundlage',
      locked: 'Erhalten Sie Ihre vollständige Entscheidungsgrundlage',
      features: [
        'Lagebild: was das Ergebnis wirklich aussagt',
        'Unsicherheiten: was das Ergebnis kippen kann',
        'Entscheidungsbedarf: reicht es für eine Entscheidung?',
        'Nächste Schritte: 3-5 priorisierte Maßnahmen',
        'PDF-Entscheidungsbrief zur Dokumentation'
      ],
      cta: 'Entscheidungsgrundlage aktivieren',
      price: '149 SEK',
      oneTime: 'Einmalkosten pro Berechnung',
      comingSoon: 'Demnächst verfügbar'
    },
    
    save: {
      title: 'Speichern & wiederkommen',
      saveBtn: 'Berechnung speichern',
      compareBtn: 'Mit anderem Fall vergleichen',
      loginRequired: 'Einloggen zum Speichern',
      saved: 'Gespeichert!',
      savedCalculations: 'Meine gespeicherten Berechnungen'
    },
    
    premiumContent: {
      lagesbild: {
        title: 'Lagebild',
        placeholder: 'Detaillierte Analyse des Immobilienwerts und der Marktposition.'
      },
      osakerheter: {
        title: 'Unsicherheiten',
        placeholder: 'Faktoren, die den Wert nach oben oder unten beeinflussen können.'
      },
      beslutsbehov: {
        title: 'Entscheidungsbedarf',
        question: 'Reicht dies für eine Entscheidung?',
        yes: 'Ja',
        no: 'Nein',
        partial: 'Teilweise'
      },
      nastaSteg: {
        title: 'Nächste Schritte',
        placeholder: 'Priorisierte Maßnahmen für das weitere Vorgehen.'
      },
      pdf: {
        title: 'Entscheidungsbrief',
        download: 'Entscheidungsbrief herunterladen (PDF)'
      }
    },
    
    tiers: {
      free: 'Kostenlos',
      registered: 'Registriert',
      premium: 'Premium'
    }
  },
  
  en: {
    resultat: {
      title: 'Result',
      estimate: 'Estimate',
      range: 'Value range',
      basedOn: 'Based on',
      hectares: 'ha productive forest land',
      m3sk: 'm³ timber stock'
    },
    
    interpretation: {
      title: 'What the result means',
      regional: 'Compared to region',
      aboveAverage: 'Above regional average',
      belowAverage: 'Below regional average',
      atAverage: 'Near regional average',
      timberDriven: 'Timber stock drives value more than location.',
      landDriven: 'Location drives value more than timber stock.',
      balanced: 'Balance between timber and land value.'
    },
    
    uncertainty: {
      title: 'How certain is the result?',
      level: 'Uncertainty level',
      low: 'Low',
      medium: 'Medium',
      high: 'High',
      factors: 'Key factors:',
      volumeNotVerified: 'Timber volume: estimated, not inventoried',
      priceRegional: 'Price level: based on regional average',
      marketFactors: 'Market factors: not included',
      planMissing: 'Forest management plan: missing',
      rangeExplanation: 'The ±15% range reflects normal variation'
    },
    
    missing: {
      title: 'What is missing',
      intro: 'For a more certain decision:',
      inventory: 'Actual timber volume (inventory)',
      forestPlan: 'Current forest management plan',
      marketInterest: 'Concrete buyer/seller interest',
      localAssessment: 'Local market analysis',
      siteQuality: 'Site quality assessment'
    },
    
    direction: {
      title: 'Possible decision direction',
      sell: 'Sell',
      sellDesc: 'if market conditions are right',
      keep: 'Keep',
      keepDesc: 'if no urgent needs',
      divide: 'Divide',
      divideDesc: 'requires deeper review',
      compare: 'Compare further',
      compareDesc: 'obtain more valuations',
      recommendation: 'With current uncertainty: deepen the basis before deciding.'
    },
    
    premium: {
      title: 'Decision State',
      locked: 'Get your complete decision basis',
      features: [
        'Situation: what the result actually says',
        'Uncertainties: what could tip the outcome',
        'Decision need: is this enough to decide?',
        'Next steps: 3-5 prioritized actions',
        'PDF decision letter for documentation'
      ],
      cta: 'Activate Decision State',
      price: '149 SEK',
      oneTime: 'One-time cost per calculation',
      comingSoon: 'Coming soon'
    },
    
    save: {
      title: 'Save & return',
      saveBtn: 'Save calculation',
      compareBtn: 'Compare with another case',
      loginRequired: 'Log in to save',
      saved: 'Saved!',
      savedCalculations: 'My saved calculations'
    },
    
    premiumContent: {
      lagesbild: {
        title: 'Situation',
        placeholder: 'Detailed analysis of property value and market position.'
      },
      osakerheter: {
        title: 'Uncertainties',
        placeholder: 'Factors that could affect the value up or down.'
      },
      beslutsbehov: {
        title: 'Decision need',
        question: 'Is this enough to decide?',
        yes: 'Yes',
        no: 'No',
        partial: 'Partially'
      },
      nastaSteg: {
        title: 'Next steps',
        placeholder: 'Prioritized actions to move forward.'
      },
      pdf: {
        title: 'Decision letter',
        download: 'Download decision letter (PDF)'
      }
    },
    
    tiers: {
      free: 'Free',
      registered: 'Registered',
      premium: 'Premium'
    }
  },
  
  se: {
    resultat: {
      title: 'Boađus',
      estimate: 'Árvvoštallan',
      range: 'Árvvospádda',
      basedOn: 'Vuođđuduvvon',
      hectares: 'ha buvttadeaddji vuovdeeana',
      m3sk: 'm³ muorravoarra'
    },
    
    interpretation: {
      title: 'Maid boađus mearkkaša',
      regional: 'Veardáduvvon guovlluin',
      aboveAverage: 'Badjel guovlogaskamearri',
      belowAverage: 'Vuolil guovlogaskamearri',
      atAverage: 'Lahka guovlogaskamearri',
      timberDriven: 'Muorravoarra vuoddá árvvu eambbo go sajádat.',
      landDriven: 'Sajádat vuoddá árvvu eambbo go muorravoarra.',
      balanced: 'Dássedeaddu muorra- ja eanadárvvu gaskkas.'
    },
    
    uncertainty: {
      title: 'Man sihkkaris lea boađus?',
      level: 'Eahpesihkkarisdássi',
      low: 'Vuollegis',
      medium: 'Gaskamearálaš',
      high: 'Alla',
      factors: 'Dehálaš faktorat:',
      volumeNotVerified: 'Muorravoarra: árvvoštallon, ii inventárisejuvvon',
      priceRegional: 'Haddedássi: vuođđuduvvon guovlogaskamearri',
      marketFactors: 'Márkanfaktorat: eai leat mielde',
      planMissing: 'Vuovdedoalloplána: váilu',
      rangeExplanation: 'Spádda ±15% govve dábálaš variašuvnna'
    },
    
    missing: {
      title: 'Mii váilu',
      intro: 'Sihkkarit mearrádussii dárbbašuvvo:',
      inventory: 'Duohta muorravoarra (inventára)',
      forestPlan: 'Ođđa vuovdedoalloplána',
      marketInterest: 'Konkrehta oastit-/vuvdisberošturmmi',
      localAssessment: 'Báikkálaš márkananalysa',
      siteQuality: 'Bonitehtaárvvoštallan'
    },
    
    direction: {
      title: 'Vejolaš mearrádusrávda',
      sell: 'Vuovdit',
      sellDesc: 'jus márkandilli lea riekta',
      keep: 'Doallat',
      keepDesc: 'jus ii leat huhti dárbu',
      divide: 'Juohkit',
      divideDesc: 'gáibida čiekŋalit geahččaleami',
      compare: 'Veardát viidásit',
      compareDesc: 'háhkat eanet árvvoštallamiid',
      recommendation: 'Dálá eahpesihkkarisain: čiekŋat vuođu ovdal mearrádusa.'
    },
    
    premium: {
      title: 'Mearrádusláhki',
      locked: 'Oaččo du olles mearrádusvuođu',
      features: [
        'Dilli: maid boađus duođaid dadjá',
        'Eahpesihkkaristagat: mii sáhttá jorggihit bohtosat',
        'Mearrádusdárbu: lea go dát doarvái mearridit?',
        'Boahtte lávkkit: 3-5 vuoruhuvvon doaimma',
        'PDF-mearrádusgirji dokumentašuvdnii'
      ],
      cta: 'Aktivere mearrádusláge',
      price: '149 kr',
      oneTime: 'Oktageardásaš gollu rehkenastaga ektui',
      comingSoon: 'Boahtá fargga'
    },
    
    save: {
      title: 'Vurke & máhcat',
      saveBtn: 'Vurke rehkenastaga',
      compareBtn: 'Veardát eará áššiin',
      loginRequired: 'Čálit sisa vurket',
      saved: 'Vurkejuvvon!',
      savedCalculations: 'Mu vurkejuvvon rehkenastagat'
    },
    
    premiumContent: {
      lagesbild: {
        title: 'Dillegovva',
        placeholder: 'Čiekŋalit analysa opmodatárvvus ja márkanpositiivnnas.'
      },
      osakerheter: {
        title: 'Eahpesihkkaristagat',
        placeholder: 'Faktorat mat sáhttet váikkuhit árvvu bajás dahje vulos.'
      },
      beslutsbehov: {
        title: 'Mearrádusdárbu',
        question: 'Lea go dát doarvái mearridit?',
        yes: 'Jua',
        no: 'Ii',
        partial: 'Oasit'
      },
      nastaSteg: {
        title: 'Boahtte lávkkit',
        placeholder: 'Vuoruhuvvon doaimmat ovdáneami várás.'
      },
      pdf: {
        title: 'Mearrádusgirji',
        download: 'Viečča mearrádusgirjji (PDF)'
      }
    },
    
    tiers: {
      free: 'Nuvttá',
      registered: 'Registrerejuvvon',
      premium: 'Premium'
    }
  }
};

export default beslutslageTranslations;
